import { CheckCircle, X } from 'lucide-react'
import { useEffect, useState } from 'react'

interface SuccessBannerProps {
  message: string | null
  onDismiss: () => void
}

// Screen readers often skip a live region that is inserted already holding
// its text, which is what happens after a redirect or once a detail page
// finishes loading. The region therefore mounts empty and receives the
// message on a later tick, so the change is announced.
const ANNOUNCE_DELAY_MS = 100

export function SuccessBanner({ message, onDismiss }: SuccessBannerProps) {
  const [announcement, setAnnouncement] = useState('')

  useEffect(() => {
    // Clear at once, so a dismissed message can't be read out afterwards;
    // only a new message waits.
    setAnnouncement('')
    if (!message) return
    const timer = setTimeout(() => {
      setAnnouncement(message)
    }, ANNOUNCE_DELAY_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [message])

  return (
    <>
      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>
      {message && (
        <div className="mb-6 rounded-lg border p-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <div className="flex items-start gap-3">
            <CheckCircle
              aria-hidden="true"
              className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-600 dark:text-green-400"
            />
            <p className="flex-1 text-sm text-green-800 dark:text-green-200">{message}</p>
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss success message"
              className="flex-shrink-0 -mt-0.5 -mr-1 p-1 rounded text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40 hover:text-green-800 dark:hover:text-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  )
}
